// src/components/Layout/Footer.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: "Impact Generation Africa",
      content: (
        <>
          <p className="text-light opacity-80 mb-4" style={{ lineHeight: '1.6' }}>
            Building sustainable food security and climate resilience through 
            community-based initiatives, youth empowerment, and grassroots innovation 
            across African communities.
          </p>
          <div className="social-links d-flex gap-3">
            {[
              { icon: '📘', name: 'Facebook', url: 'https://web.facebook.com/profile.php?id=100068962459906' },
              { icon: '🐦', name: 'Twitter', url: '#' },
              { icon: '📷', name: 'Instagram', url: '#' },
              { icon: '💼', name: 'LinkedIn', url: '#' },
              { icon: '📺', name: 'YouTube', url: '#' }
            ].map((social) => (
              <motion.a
                key={social.name}
                href={social.url}
                className="text-light opacity-70 hover-lift"
                whileHover={{ scale: 1.2, opacity: 1 }}
                whileTap={{ scale: 0.9 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <span className="fs-5">{social.icon}</span>
              </motion.a>
            ))}
          </div>
        </>
      )
    },
    {
      title: "Quick Links",
      links: [
        { name: "About Us", path: "/about" },
        { name: "Our Programs", path: "/programs" },
        { name: "Get Involved", path: "/volunteer" },
        { name: "Success Stories", path: "/blog" },
        { name: "Contact Us", path: "/contact" }
      ]
    },
    {
      title: "Our Programs",
      links: [
        { name: "Food Security", path: "/programs/food-security" },
        { name: "Climate Action", path: "/programs/climate-action" },
        { name: "Youth Empowerment", path: "/programs/youth-empowerment" },
        { name: "Women Empowerment", path: "/programs/women-empowerment" },
        { name: "Carbon Credit", path: "/carbon-credit" }
      ]
    },
    {
      title: "Get Involved",
      links: [
        { name: "Volunteer", path: "/volunteer" },
        { name: "Donate", path: "/donate" },
        { name: "Partnership", path: "/partnership" },
        { name: "Careers", path: "/careers" },
        { name: "Events", path: "/events" }
      ]
    },
    {
      title: "Contact Info",
      content: (
        <div className="text-light opacity-80">
          <div className="d-flex align-items-start mb-3">
            <span className="text-success me-3">📍</span>
            <div>
              <div className="fw-semibold">Headquarters</div>
              <div className="small">Kapenguria West Pokot, Kenya</div>
            </div>
          </div>
          <div className="d-flex align-items-start mb-3">
            <span className="text-success me-3">📧</span>
            <div>
              <div className="fw-semibold">Email Us</div>
              <div className="small">info@impactgenerationafrica.org</div>
            </div>
          </div>
          <div className="d-flex align-items-start mb-3">
            <span className="text-success me-3">📞</span>
            <div>
              <div className="fw-semibold">Call Us</div>
              <div className="small">+254 703 543 030</div>
            </div>
          </div>
          <div className="d-flex align-items-start">
            <span className="text-success me-3">🕒</span>
            <div>
              <div className="fw-semibold">Office Hours</div>
              <div className="small">Mon - Fri: 8:00 AM - 5:00 PM</div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <footer className="bg-dark text-light position-relative overflow-hidden">
      {/* Background Pattern */}
      <div className="position-absolute top-0 start-0 w-100 h-100 opacity-5">
        <div 
          className="position-absolute"
          style={{
            top: '10%',
            left: '5%',
            opacity: 0.1,
            fontSize: '8rem',
            transform: 'rotate(45deg)'
          }}
        >
          🌱
        </div>
        <div 
          className="position-absolute"
          style={{
            bottom: '10%',
            right: '5%',
            fontSize: '6rem',
            transform: 'rotate(-45deg)'
          }}
        >
          🌍
        </div>
      </div>

      <div className="container position-relative z-2">
        {/* Main Footer Content */}
        <motion.div 
          className="row py-5"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {footerSections.map((section) => (
            <motion.div
              key={section.title}
              className="col-lg-3 col-md-6 mb-5"
              variants={itemVariants}
            >
              <h6 className="text-success fw-bold mb-4 text-uppercase small">
                {section.title}
              </h6>
              
              {section.links ? (
                <ul className="list-unstyled">
                  {section.links.map((link) => (
                    <motion.li 
                      key={link.name} 
                      className="mb-2"
                      whileHover={{ x: 5 }}
                      transition={{ type: "spring", stiffness: 400 }}
                    >
                      <Link 
                        to={link.path}
                        className="text-light opacity-80 text-decoration-none d-flex align-items-center hover-lift"
                      >
                        <span className="text-success me-2">›</span>
                        {link.name}
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              ) : (
                section.content
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* Newsletter Subscription */}
        <motion.div 
          className="row py-4 border-top border-secondary"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <div className="col-lg-6 mb-3 mb-lg-0">
            <h6 className="text-success fw-bold mb-3">Stay Updated</h6>
            <p className="text-light opacity-80 mb-3 small">
              Subscribe to our newsletter for updates on our programs, success stories, and opportunities to get involved.
            </p>
            <div className="d-flex gap-2">
              <input 
                type="email" 
                placeholder="Enter your email"
                className="form-control form-control-sm bg-dark border-secondary text-light"
                style={{ maxWidth: '250px' }}
              />
              <motion.button
                className="btn btn-success btn-sm px-3"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Subscribe
              </motion.button>
            </div>
          </div>
          
          <div className="col-lg-6 text-lg-end">
            <h6 className="text-success fw-bold mb-3">Support Our Mission</h6>
            <p className="text-light opacity-80 mb-3 small">
              Your support helps us create sustainable impact in communities across Africa.
            </p>
            <motion.div className="d-flex gap-3 justify-content-lg-end">
              <motion.a
                href="/donate"
                className="btn btn-outline-success btn-sm px-4"
                whileHover={{ scale: 1.05, backgroundColor: '#198754', color: 'white' }}
                whileTap={{ scale: 0.95 }}
              >
                💚 Donate Now
              </motion.a>
              <motion.a
                href="/volunteer"
                className="btn btn-outline-light btn-sm px-4"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                🤝 Volunteer
              </motion.a>
            </motion.div>
          </div>
        </motion.div>

        {/* Impact Stats */}
        <motion.div 
          className="row py-4 border-top border-secondary"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <div className="col-12">
            <div className="row text-center">
              {[
                { value: '10K+', label: 'Lives Impacted' },
                { value: '10+', label: 'Communities' },
                { value: '10K+', label: 'Trees Planted' },
                { value: '5K+', label: 'Youth Trained' },
                { value: '2K+', label: 'Women Empowered' }
              ].map((stat) => (
                <motion.div
                  key={stat.label}
                  className="col-6 col-md-4 col-lg-2-4 mb-3"
                  variants={itemVariants}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="text-success fw-black fs-4 mb-1">{stat.value}</div>
                  <div className="text-light opacity-80 small">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Bottom Footer */}
        <motion.div 
          className="row py-4 border-top border-secondary"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <div className="col-md-6 text-center text-md-start mb-2 mb-md-0">
            <p className="text-light opacity-70 mb-0 small">
              &copy; {currentYear} Impact Generation Africa. All rights reserved.
            </p>
          </div>
          <div className="col-md-6 text-center text-md-end">
            <div className="d-flex justify-content-center justify-content-md-end gap-4 small">
              <Link to="/privacy" className="text-light opacity-70 text-decoration-none">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-light opacity-70 text-decoration-none">
                Terms of Service
              </Link>
              <Link to="/sitemap" className="text-light opacity-70 text-decoration-none">
                Sitemap
              </Link>
              <span className="text-light opacity-70">
                Built with ❤️ for a sustainable Africa
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Custom Styles */}
      <style>{`
        .hover-lift {
          transition: all 0.3s ease;
        }
        
        .hover-lift:hover {
          transform: translateY(-2px);
          opacity: 1 !important;
        }
        
        .col-lg-2-4 {
          flex: 0 0 auto;
          width: 20%;
        }
        
        @media (max-width: 992px) {
          .col-lg-2-4 {
            width: 33.333%;
          }
        }
        
        @media (max-width: 768px) {
          .col-lg-2-4 {
            width: 50%;
          }
        }
        
        @media (max-width: 576px) {
          .col-lg-2-4 {
            width: 100%;
          }
        }
        
        .border-secondary {
          border-color: #6c757d !important;
        }
      `}</style>
    </footer>
  );
};

export default Footer;